import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Heart, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * NonprofitDonationCard — mounted on any public vendor page whose user is
 * a verified 501(c)(3). Shows a Donate CTA + running "$X raised" total.
 * Returns null when the vendor isn't a verified nonprofit OR hasn't set up
 * Stripe Connect (accepts_donations=false).
 */
export default function NonprofitDonationCard({ vendor, onDonateClick }) {
  const [summary, setSummary] = useState(null);
  const isVerified = vendor?.nonprofit_status === "verified";

  useEffect(() => {
    if (!isVerified || !vendor?.id) return;
    api.get(`/nonprofits/${vendor.id}/donations/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  }, [vendor?.id, isVerified]);

  if (!isVerified) return null;
  if (summary && !summary.accepts_donations) return null;
  // Also skip while summary is still loading and we can't confirm connect status.
  // (We render optimistically only when the flag came through — otherwise we
  // wait so we don't flash a Donate button that immediately errors.)
  if (summary === null) return null;

  const raised = summary?.total_raised_cents || 0;
  const donors = summary?.donor_count || 0;

  return (
    <section
      data-testid="public-vendor-donate-card"
      className="card-warm rounded-sm p-5 sm:p-6 mt-4 border-2"
      style={{ borderColor: "var(--avt-pink)" }}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: "rgba(236,72,153,0.14)" }}>
          <Heart className="h-5 w-5" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest"
               style={{ color: "var(--avt-pink)" }}>
            <ShieldCheck className="h-3 w-3" /> Verified 501(c)(3)
          </div>
          <div className="font-serif text-xl mt-0.5">Support this nonprofit</div>
          <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
            100% of your gift goes to {summary?.org_name || vendor.business_name || "this org"}. AddVendTour takes 0%.
          </p>
        </div>
      </div>

      {raised > 0 && (
        <div className="mt-4 flex items-baseline gap-3">
          <span className="font-serif text-2xl" style={{ color: "var(--avt-purple)" }} data-testid="public-vendor-donate-raised">
            ${(raised / 100).toLocaleString()}
          </span>
          <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
            raised from {donors} {donors === 1 ? "supporter" : "supporters"}
          </span>
        </div>
      )}

      <Button
        data-testid="public-vendor-donate-btn"
        onClick={onDonateClick}
        className="btn-primary rounded-full w-full mt-4 py-5"
      >
        <Heart className="h-4 w-4 mr-2" fill="#fff" strokeWidth={0} /> Donate now
      </Button>
    </section>
  );
}
